import { useAccordionItemContext } from '@chakra-ui/react';
import { IOfferingSummaryResSchema } from '@monetize/types/app';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MAccordionPanel,
  MBox,
  MSkeleton,
  MTable,
  MTbody,
  MTd,
  MText,
  MTh,
  MThead,
  MTr,
} from '../../../components/Monetize';
import { OfferingLookupPopover } from '../../../components/Monetize/LookupPopover';
import { OFFERING_STATUS_DISPLAY } from '../../../constants/offerings';

function RelatedOfferingsTableSkeleton() {
  return (
    <MBox w="full">
      {Array.from({ length: 5 }, (_, index) => (
        <MSkeleton
          key={index}
          height="1rem"
          width="100%"
          mt={index === 0 ? 0 : 2}
        />
      ))}
    </MBox>
  );
}

export const getRowColor = (index: number) =>
  index % 2 === 0 ? 'tWhite.titanWhite' : 'tWhite.base';

interface RelatedOfferingsSectionProps {
  offerings: IOfferingSummaryResSchema[];
  isLoading?: boolean;
  onAccordionOpen?: () => void;
}

interface RelatedOfferingsSectionBodyProps {
  offerings: IOfferingSummaryResSchema[];
  isLoading: boolean;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const RelatedOfferingsSectionBody = ({
  offerings,
  isLoading,
}: RelatedOfferingsSectionBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        label="Related Offerings"
      />
      <MAccordionPanel px={6} py={1}>
        {isLoading ? (
          <RelatedOfferingsTableSkeleton />
        ) : offerings.length > 0 ? (
          <MBox w="full">
            <MTable size="sm">
              <MThead>
                <MTr>
                  <MTh fontSize="xs">Name</MTh>
                  <MTh fontSize="xs">Description</MTh>
                  <MTh fontSize="xs">Status</MTh>
                </MTr>
              </MThead>
              <MTbody>
                {offerings.map((offering, index) => (
                  <MTr key={offering.id} bg={getRowColor(index)}>
                    <MTd>
                      <OfferingLookupPopover
                        offeringId={offering.id}
                        label={offering.name}
                      />
                    </MTd>
                    <MTd>
                      <MText color="tPurple.base" lineClamp={3} maxW="233px">
                        {offering.description ?? ''}
                      </MText>
                    </MTd>
                    <MTd>
                      <MText color="tPurple.base" textTransform="capitalize">
                        {OFFERING_STATUS_DISPLAY[offering.status]}
                      </MText>
                    </MTd>
                  </MTr>
                ))}
              </MTbody>
            </MTable>
          </MBox>
        ) : (
          <MText color="tGray.500" fontSize="sm">
            This product is not used in any offerings.
          </MText>
        )}
      </MAccordionPanel>
    </>
  );
};

export function RelatedOfferingsSection({
  offerings,
  isLoading = false,
  onAccordionOpen,
}: RelatedOfferingsSectionProps) {
  return (
    <MBox w="100%">
      <MAccordion
        collapsible
        onValueChange={({ value }) => {
          if (value.length > 0 && onAccordionOpen) {
            onAccordionOpen();
          }
        }}
      >
        <MAccordionItem value="0" borderColor="#C8C5D4">
          <RelatedOfferingsSectionBody
            offerings={offerings}
            isLoading={isLoading}
          />
        </MAccordionItem>
      </MAccordion>
    </MBox>
  );
}
